import { Checkbox, Modal } from '@carbon/react';
import { useEffect, useRef, useState } from 'react';
import { format0 } from 'utils/number';
import {
  FORMAT_OPTIONS,
  SPATIAL_EXPORT_VALIDATION_HINT,
  buildSelection,
  canExport,
  defaultSelection,
  exceedsFeatureCap,
  selectedFeatureCount,
} from './model';
import { SpatialLayerPicker } from './spatial-layer-picker';
import type {
  SpatialExportSelection,
  SpatialFormat,
  SpatialLayerOption,
} from './types';
import styles from './export-spatial-data.module.scss';

export interface ExportSpatialDataProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Dismiss the dialog (Cancel / Esc / backdrop). */
  onClose: () => void;
  /** The hosting surface's exportable layers (roster). */
  layers: SpatialLayerOption[];
  /** Called with the chosen layers/attributes/formats when Export is confirmed. */
  onExport: (selection: SpatialExportSelection) => void;
  /** Modal heading; defaults to "Export spatial data". */
  title?: string;
  /** True while a submit is in flight — disables the footer and shows the in-flight label. */
  isExporting?: boolean;
  /**
   * The combined feature count above which the export is BLOCKED — a hard cap (see
   * SPATIAL_EXPORT_MAX_FEATURES), not a confirmation. When the selected layers' known total exceeds
   * it, Export is disabled and the dialog asks the user to narrow the selection, so a doomed
   * whole-dataset export is never submitted. Omit to export any size straight through (surfaces
   * whose populations are always small).
   */
  maxFeatures?: number;
}

/**
 * The shared spatial data export dialog. Surface-agnostic and prop-driven: it lists the hosting
 * surface's exportable layers as parent checkboxes (name + record count + attribute count), each
 * expanding to an inline grid of attribute checkboxes, plus the formats group, and hands the
 * selection back via `onExport`. The surface owns submission (endpoint, async status, toast).
 *
 * Behaviour per the product spec: defaults re-seed on every open (all layers, all attributes,
 * Shapefile only — never remembers a prior export); geometry is always included, so a layer with
 * no attributes checked is a valid geometry-only export; Export is disabled with a hint until at
 * least one layer and one format are selected; every checked layer is exported in every format.
 * When `maxFeatures` is set and the selected layers' known total exceeds it, Export is disabled and
 * the dialog asks the user to narrow the selection (a hard cap, not a confirmation).
 *
 * @param {ExportSpatialDataProps} props - The roster, the submit/close actions, and in-flight state.
 * @return {JSX.Element} The modal.
 */
export function ExportSpatialData({
  open,
  onClose,
  layers,
  onExport,
  title = 'Export spatial data',
  isExporting = false,
  maxFeatures,
}: ExportSpatialDataProps) {
  const [layerIds, setLayerIds] = useState<string[]>([]);
  const [attributesByLayer, setAttributesByLayer] = useState<
    Record<string, string[]>
  >({});
  const [formats, setFormats] = useState<SpatialFormat[]>([]);

  // Re-seed defaults on the closed→open transition only, so selections are never remembered
  // across opens, yet a layers/count refresh while open never wipes the user's in-progress choices.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      const defaults = defaultSelection(layers);
      setLayerIds(defaults.layerIds);
      setAttributesByLayer(defaults.attributesByLayer);
      setFormats(defaults.formats);
    }
    wasOpen.current = open;
  }, [open, layers]);

  const valid = canExport(layerIds, formats);
  const blocked = exceedsFeatureCap(layers, layerIds, maxFeatures);
  const total = selectedFeatureCount(layers, layerIds);

  /**
   * Hand the current selection to `onExport`, unless it is invalid, in flight, or over the hard cap
   * (`maxFeatures`) — the footer is disabled in those cases, and this guards the keyboard path too.
   *
   * @return {void}
   */
  const submit = () => {
    if (!valid || isExporting || blocked) return;
    onExport(buildSelection(layerIds, attributesByLayer, formats));
  };

  return (
    <Modal
      open={open}
      size="md"
      modalHeading={title}
      primaryButtonText={isExporting ? 'Exporting…' : 'Export'}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={!valid || isExporting || blocked}
      onRequestSubmit={submit}
      onSecondarySubmit={onClose}
      onRequestClose={onClose}
    >
      <>
        <p className={styles.hint}>
          Check the attributes to export for each layer — geometry is always
          included. Every checked layer is exported in each selected format.
          Each layer exports the records matching its current filters (the count
          shown), so filter a dataset first to export fewer. Coordinate system:
          WGS84 (EPSG:4326).
        </p>

        <SpatialLayerPicker
          layers={layers}
          layerIds={layerIds}
          attributesByLayer={attributesByLayer}
          onChange={({
            layerIds: nextLayerIds,
            attributesByLayer: nextAttributesByLayer,
          }) => {
            setLayerIds(nextLayerIds);
            setAttributesByLayer(nextAttributesByLayer);
          }}
        />

        <div role="group" aria-label="Formats" className={styles.formats}>
          <strong className={styles.formatsLabel}>Formats</strong>
          <div className={styles.formatOptions}>
            {FORMAT_OPTIONS.map((format) => (
              <Checkbox
                key={format.id}
                id={`spatial-format-${format.id}`}
                labelText={format.label}
                checked={formats.includes(format.id)}
                onChange={(_event, { checked }) =>
                  setFormats((prev) =>
                    checked
                      ? [...new Set([...prev, format.id])]
                      : prev.filter((id) => id !== format.id)
                  )
                }
              />
            ))}
          </div>
        </div>

        {blocked && (
          <p className={styles.blocked} role="alert">
            This selection matches about {format0(total)} records, over the{' '}
            {format0(maxFeatures ?? 0)} limit for one export. Narrow it with
            filters, an area polygon, or by zooming the map in, then try again.
          </p>
        )}

        {!valid && !isExporting && (
          <p className={styles.validation} role="alert">
            {SPATIAL_EXPORT_VALIDATION_HINT}
          </p>
        )}
      </>
    </Modal>
  );
}
